import { DateTime, Interval } from 'luxon';

import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import {
  DATE_AND_TIME_NO_YEAR_TZ_FORMAT,
  DATE_AND_TIME_TZ_FORMAT,
  DAYS_HOURS_HUMAN_FORMAT,
  HOURS_MINUTES_HUMAN_FORMAT,
  MILLISECONDS_ELAPSED_FORMAT,
  MINUTES_SECONDS_HUMAN_FORMAT,
  SECONDS_MILLISECONDS_ELAPSED_FORMAT,
  TIME_FORMAT,
  TIME_FORMAT_NO_SECONDS,
  WEEKS_DAYS_HUMAN_FORMAT,
} from 'lib/constants/luxonFormats';

export const startEndTime = (
  steps: WorkflowRunStep[],
): {
  startTime: DateTime;
  endTime: DateTime;
} => {
  const startTime = DateTime.fromISO(
    // Prefers enqueue time since pre-lazily-initization, steps could be created long before the actual processing happens
    steps[0]['attributes']['enqueued-at'] || steps[0]['attributes']['created-at'],
  );
  const endTime = DateTime.fromISO(
    // Prefers completed time since steps could be updated outside of normal flow.
    steps[steps.length - 1]['attributes']['completed-at'] ||
      steps[steps.length - 1]['attributes']['updated-at'],
  );

  return { startTime, endTime };
};

export const humanReadableRunTime = (
  startTime: DateTime,
  endTime: DateTime,
): {
  startTimeString: string;
  endTimeString: string;
  durationString: string;
  timestampFormat: string;
} => {
  const interval = Interval.fromDateTimes(startTime, endTime);

  if (startTime.year !== DateTime.local().year || startTime.year !== endTime.year) {
    const format = DATE_AND_TIME_TZ_FORMAT;
    return {
      startTimeString: startTime.toFormat(format),
      endTimeString: endTime.toFormat(format),
      durationString: formatInterval(interval),
      timestampFormat: format,
    };
  }

  if (interval.length('days') > 1) {
    const format = DATE_AND_TIME_NO_YEAR_TZ_FORMAT;
    return {
      startTimeString: startTime.toFormat(format),
      endTimeString: endTime.toFormat(format),
      durationString: formatInterval(interval),
      timestampFormat: format,
    };
  }

  if (interval.length('hours') > 1) {
    const format = TIME_FORMAT_NO_SECONDS;
    return {
      startTimeString: startTime.toFormat(format),
      endTimeString: endTime.toFormat(format),
      durationString: formatInterval(interval),
      timestampFormat: format,
    };
  }

  const format = TIME_FORMAT;
  if (interval.length('minutes') > 1) {
    return {
      startTimeString: startTime.toFormat(format),
      endTimeString: endTime.toFormat(format),
      durationString: formatInterval(interval),
      timestampFormat: format,
    };
  }

  if (interval.length('seconds') > 1) {
    return {
      startTimeString: startTime.toFormat(format),
      endTimeString: endTime.toFormat(format),
      durationString: formatInterval(interval),
      timestampFormat: format,
    };
  }

  return {
    startTimeString: startTime.toFormat(format),
    endTimeString: endTime.toFormat(format),
    durationString: formatInterval(interval),
    timestampFormat: format,
  };
};

const formatInterval = (interval: Interval): string => {
  const duration = interval.toDuration();
  if (interval.length('years') > 1) {
    return duration.toFormat(WEEKS_DAYS_HUMAN_FORMAT);
  } else if (interval.length('days') > 1) {
    return duration.toFormat(DAYS_HOURS_HUMAN_FORMAT);
  } else if (interval.length('hours') > 1) {
    return duration.toFormat(HOURS_MINUTES_HUMAN_FORMAT);
  } else if (interval.length('minutes') > 1) {
    return duration.toFormat(MINUTES_SECONDS_HUMAN_FORMAT);
  } else if (interval.length('seconds') > 1) {
    return duration.toFormat(SECONDS_MILLISECONDS_ELAPSED_FORMAT);
  } else {
    return duration.toFormat(MILLISECONDS_ELAPSED_FORMAT);
  }
};
